import React from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import DefaultAvatarImage from "./DefaultAvatarImage";

import type { JSX } from "react";

export interface AvatarStackItem {
  id: string;
  name: string | null;
  avatarUrl?: string | null;
}

export function getInitials(name: string | null): string {
  if (!name) return "";

  const [first = "", second = ""] = name.trim().split(/\s+/);
  const initials = second
    ? `${first.charAt(0)}${second.charAt(0)}`
    : name.slice(0, 2);
  return initials.toUpperCase();
}

interface AvatarStackProps {
  items: AvatarStackItem[];
  max?: number;
  className?: string;
}

export function AvatarStack({
  items,
  max = 2,
  className,
}: AvatarStackProps): JSX.Element | null {
  if (items.length === 0) return null;

  const visibleItems = items.slice(0, max);
  const hiddenItems = items.slice(max);
  const overflowCount = hiddenItems.length;
  const hiddenNameList = hiddenItems.flatMap((item) =>
    item.name ? [item.name] : [],
  );

  return (
    <AvatarGroup className={className}>
      {visibleItems.map((item) => (
        <Avatar
          key={item.id}
          data-tooltip-content={item.name ?? undefined}
          data-tooltip-id={item.name ? APP_TOOLTIP_ID : undefined}
          size="sm"
        >
          <AvatarImage alt="" src={item.avatarUrl ?? undefined} />
          <AvatarFallback>
            {getInitials(item.name) || <DefaultAvatarImage />}
          </AvatarFallback>
        </Avatar>
      ))}
      {overflowCount > 0 ? (
        <AvatarGroupCount
          data-tooltip-content={hiddenNameList.join(", ") || undefined}
          data-tooltip-id={
            hiddenNameList.length > 0 ? APP_TOOLTIP_ID : undefined
          }
        >
          +{overflowCount}
        </AvatarGroupCount>
      ) : null}
    </AvatarGroup>
  );
}
